import axios from 'axios'
import type { Paginated } from '../types/pagination'
import type { Attachment } from '../types/project'
import { InvitationStatus } from '../types/project'
import type {
  DriverAssignmentRequest,
  DriverAssignmentResponse,
  WorkOrder,
  WorkOrderAcceptData,
  WorkOrderInvite,
  WorkOrderReport,
  WorkOrderStatus,
  WorkOrderStatusUpdateData,
} from '../types/workorder'
import { api } from './fetch'
import { projectService } from './projectService'

interface WorkOrderFilters {
  dateFrom?: Date
  dateTo?: Date
  search?: string
  status?: WorkOrderStatus
  projectId?: number
}

// In-flight request cache to deduplicate concurrent requests
const inFlightRequests = new Map<string, Promise<unknown>>()

interface GenerateLabelResponse {
  label: string
  creation_date_formatted: string
}

class WorkOrderService {
  /**
   * Generate a preview workorder label and creation date for the confirmation step.
   */
  async generateLabel(): Promise<GenerateLabelResponse> {
    const response = await api.get<GenerateLabelResponse>(
      '/workorder/generate-label/',
    )
    return response.data
  }

  /**
   * Send a draft work order - transitions DRAFT -> PENDING and sends invitations.
   */
  async sendDraft(workOrderId: number | string): Promise<WorkOrder> {
    try {
      const response = await api.post<WorkOrder>(
        `/workorder/${workOrderId}/send/`,
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Failed to send draft work order:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          message: error.message,
          details:
            error.response?.data?.detail || error.response?.data?.message,
        })
      } else {
        console.error('Error sending draft work order:', error)
      }
      throw error
    }
  }

  /**
   * Delete a draft work order.
   */
  async deleteWorkOrder(
    workOrderId: number | string,
  ): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.delete<{ success: boolean; message: string }>(
        `/workorder/${workOrderId}/`,
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Failed to delete work order:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          message: error.message,
          details:
            error.response?.data?.detail || error.response?.data?.message,
        })
      } else {
        console.error('Error deleting work order:', error)
      }
      throw error
    }
  }

  async getWorkOrders(
    filters?: WorkOrderFilters & { page?: number; page_size?: number },
  ): Promise<Paginated<WorkOrder>> {
    const params = new URLSearchParams()

    if (filters?.dateFrom) {
      params.append('created_after', filters.dateFrom.toISOString())
    }
    if (filters?.dateTo) {
      params.append('created_before', filters.dateTo.toISOString())
    }
    if (filters?.search) {
      params.append('search', filters.search)
    }
    if (filters?.status) {
      params.append('status', String(filters.status))
    }
    if (filters?.projectId) {
      params.append('project_id', String(filters.projectId))
    }
    if (filters?.page) {
      params.append('page', String(filters.page))
    }
    if (filters?.page_size) {
      params.append('page_size', String(filters.page_size))
    }

    const queryString = params.toString()
    const url = queryString ? `/workorder/?${queryString}` : '/workorder/'
    const cacheKey = `getWorkOrders:${url}`

    // Return existing in-flight request if one exists
    if (inFlightRequests.has(cacheKey)) {
      return inFlightRequests.get(cacheKey) as Promise<Paginated<WorkOrder>>
    }

    // Create new request and cache it
    const requestPromise = (async () => {
      try {
        const response = await api.get<Paginated<WorkOrder> | WorkOrder[]>(url)
        const data = response.data as unknown
        if (Array.isArray(data)) {
          let items = data as WorkOrder[]
          // Apply optional server-like filters for local array responses
          if (filters?.status) {
            items = items.filter((wo) => wo.status === filters.status)
          }
          if (filters?.projectId) {
            items = items.filter((wo) => {
              const woProjectId =
                typeof wo.project === 'object' ? wo.project.id : wo.project
              return Number(woProjectId) === Number(filters.projectId)
            })
          }
          // Stable order by -created_date to mimic backend ordering
          items = [...items].sort((a, b) => {
            const da = new Date(a.created_date || '').getTime()
            const db = new Date(b.created_date || '').getTime()
            return db - da
          })
          const total = items.length
          const currentPage = Math.max(1, filters?.page ?? 1)
          const size = Math.max(1, Math.min(filters?.page_size ?? total, total))
          const start = (currentPage - 1) * size
          const end = start + size
          const paged = items.slice(start, end)
          const pages = size > 0 ? Math.max(1, Math.ceil(total / size)) : 1
          return { items: paged, count: total, page: currentPage, pages }
        }
        return data as Paginated<WorkOrder>
      } finally {
        inFlightRequests.delete(cacheKey)
      }
    })()

    inFlightRequests.set(cacheKey, requestPromise)
    return requestPromise
  }

  async getWorkOrder(id: number | string): Promise<WorkOrder> {
    const response = await api.get(`/workorder/${id}/`)
    return response.data
  }

  async createWorkOrder(data: Omit<WorkOrder, 'id'>): Promise<WorkOrder> {
    const projectId =
      typeof data.project === 'object' ? data.project?.id : data.project

    // Build workorder data, only including fields with actual values
    const workorderData: Record<string, unknown> = {
      // Project ID is required for non-draft work orders
      ...(projectId ? { project_id: projectId } : {}),
      // Arrays - only include if they have items
      ...(data.subhaulers?.length
        ? { subhaulers: data.subhaulers.map((user) => user.id) }
        : { subhaulers: [] }),
      ...(data.dispatchers?.length
        ? { dispatchers: data.dispatchers.map((user) => user.id) }
        : { dispatchers: [] }),
      // String fields - only include if they have values
      ...(data.pickup_address ? { pickup_address: data.pickup_address } : {}),
      ...(data.dropoff_address
        ? { dropoff_address: data.dropoff_address }
        : {}),
      ...(data.trucks_number ? { trucks_number: data.trucks_number } : {}),
      ...(data.truck_types?.length ? { truck_types: data.truck_types } : {}),
      ...(data.material ? { material: data.material } : {}),
      ...(data.volume ? { volume: data.volume } : {}),
      ...(data.pay_rate ? { pay_rate: data.pay_rate } : {}),
      ...(data.rate_type ? { rate_type: data.rate_type } : {}),
      ...(data.starting_date ? { starting_date: data.starting_date } : {}),
      ...(data.completion_date
        ? { completion_date: data.completion_date }
        : {}),
      ...(data.notes ? { notes: data.notes } : {}),
      // Payment schedule fields
      ...(data.payment_type ? { payment_type: data.payment_type } : {}),
      ...(data.payment_schedule?.length
        ? { payment_schedule: data.payment_schedule }
        : {}),
      ...(data.total_scheduled_payment
        ? { total_scheduled_payment: data.total_scheduled_payment }
        : {}),
      ...(data.total_scheduled_fees
        ? { total_scheduled_fees: data.total_scheduled_fees }
        : {}),
      // Status - include if provided (for creating drafts)
      ...(data.status ? { status: data.status } : {}),
      // Label - include if provided (pre-generated from confirmation step)
      ...(data.label ? { label: data.label } : {}),
      // Invitation status
      ...(data.invitation_status
        ? { invitation_status: data.invitation_status }
        : {}),
    }

    const response = await api.post('/workorder/', workorderData)
    return response.data
  }

  async updateWorkOrder(
    id: string,
    data: Partial<WorkOrder>,
  ): Promise<WorkOrder> {
    const response = await api.patch(`/workorder/${id}/`, data)
    return response.data
  }

  async markAsComplete(workorderId: number): Promise<WorkOrder> {
    const response = await api.patch<WorkOrder>(
      `/workorder/${workorderId}/complete/`,
    )
    return response.data
  }

  async updateWorkOrderStatus(
    workOrderId: string | number,
    status: WorkOrderStatus,
    declineReason?: string,
  ): Promise<WorkOrder> {
    try {
      const payload: { status: WorkOrderStatus; decline_reason?: string } = {
        status,
      }
      if (declineReason) {
        payload.decline_reason = declineReason
      }
      const response = await api.patch(
        `/workorder/${workOrderId}/status/`,
        payload,
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Failed to update work order status:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          message: error.message,
          details:
            error.response?.data?.detail || error.response?.data?.message,
        })
      } else {
        console.error('Error updating work order status:', error)
      }
      throw error
    }
  }

  async assignDrivers(
    workOrderId: string | number,
    driverIds: number[],
  ): Promise<DriverAssignmentResponse> {
    try {
      const requestData: DriverAssignmentRequest = {
        driver_ids: driverIds,
      }

      const response = await api.post(
        `/workorder/${workOrderId}/assign-drivers/`,
        requestData,
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Failed to assign drivers:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          message: error.message,
          details:
            error.response?.data?.detail || error.response?.data?.message,
        })
      } else {
        console.error('Error assigning drivers:', error)
      }
      throw error
    }
  }

  async getWorkOrderReport(workOrderId: number): Promise<WorkOrderReport> {
    try {
      const response = await api.get(`/workorder/${workOrderId}/report`)
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Failed to get work order report:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          message: error.message,
          details:
            error.response?.data?.detail || error.response?.data?.message,
        })
      } else {
        console.error('Error getting work order report:', error)
      }
      throw error
    }
  }

  async acceptWorkOrderInvite(
    workOrderId: string | number,
    inviteId: string,
  ): Promise<{ success: boolean }> {
    try {
      const response = await api.patch(
        `/workorder/${workOrderId}/invite/${inviteId}/accept`,
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Failed to accept work order invite:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          message: error.message,
          details:
            error.response?.data?.detail || error.response?.data?.message,
        })
      } else {
        console.error('Error accepting work order invite:', error)
      }
      throw error
    }
  }

  async getUserPendingWorkOrderInvitations(): Promise<WorkOrderInvite[]> {
    try {
      const response = await api.get('/workorder/invites/pending')
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Failed to get user pending work order invitations:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          message: error.message,
          details:
            error.response?.data?.detail || error.response?.data?.message,
        })
      } else {
        console.error(
          'Error getting user pending work order invitations:',
          error,
        )
      }
      throw error
    }
  }

  async downloadWorkOrderPdf(workOrderId: string | number): Promise<Blob> {
    try {
      const response = await api.get(`/workorder/${workOrderId}/download/`, {
        responseType: 'blob',
      })
      return response.data
    } catch (error) {
      console.error('Failed to download work order PDF:', error)
      throw error
    }
  }

  /**
   * Accept a work order with designated drivers.
   * This endpoint sets the work order status to ACCEPTED and creates designated driver records.
   */
  async acceptWithDrivers(
    workOrderId: string | number,
    acceptData: WorkOrderAcceptData,
  ): Promise<WorkOrder> {
    try {
      const response = await api.post(
        `/workorder/${workOrderId}/accept/`,
        acceptData,
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Failed to accept work order with drivers:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          message: error.message,
          details:
            error.response?.data?.detail || error.response?.data?.message,
        })
      } else {
        console.error('Error accepting work order with drivers:', error)
      }
      throw error
    }
  }

  /**
   * Upload attachments to a work order.
   */
  async uploadAttachments(
    workOrderId: string | number,
    files: File[],
  ): Promise<Attachment[]> {
    try {
      const formData = new FormData()
      for (const file of files) {
        formData.append('files', file)
      }

      const response = await api.post<{
        success: boolean
        message: string
        attachments: Attachment[]
      }>(`/workorder/${workOrderId}/attachments/upload/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      return response.data.attachments
    } catch (error) {
      console.error('Failed to upload attachments:', error)
      throw error
    }
  }

  /**
   * Get all attachments for a work order.
   */
  async getAttachments(workOrderId: string | number): Promise<Attachment[]> {
    try {
      const response = await api.get<Attachment[]>(
        `/workorder/${workOrderId}/attachments/`,
      )
      return response.data
    } catch (error) {
      console.error('Failed to get attachments:', error)
      throw error
    }
  }

  /**
   * Delete an attachment from a work order.
   */
  async deleteAttachment(
    workOrderId: string | number,
    attachmentId: number,
  ): Promise<void> {
    try {
      await api.delete(`/workorder/${workOrderId}/attachments/${attachmentId}/`)
    } catch (error) {
      console.error('Failed to delete attachment:', error)
      throw error
    }
  }
}

export const workOrderService = new WorkOrderService()
export type { WorkOrderFilters }
